import { ApiClientWrapper } from '@spike/api-client';
import { fetchEmailMarketingSenderThunk } from '@spike/email-marketing-action';
import { MarketplaceEmailSender } from '@spike/email-marketing-model';
import { updateInboxThunk } from '@spike/omnichannel-action';
import { Inbox, InboxSenderSource } from '@spike/omnichannel-model';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { Radio } from 'components/UI/V3/Forms/Radio';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import { validateSenderEmail } from 'components/EmailMarketing/EmailSender/utils/validateSenderEmail';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { MarketingEmailsPath } from 'routes/Paths';
import { RootState } from 'store';
import { inboxRequestErrorMessage } from './inboxRequestError';
import styles from './EmailSenderModal.module.scss';

interface EmailSenderModalProps {
    inbox: Inbox;
    /** Scoped to the selected location (threads and inboxes are per location). */
    apiClientWrapper: ApiClientWrapper;
    onCancel: () => void;
    /** Save succeeded — the inbox row in the store is fresh. Parent dismisses the modal. */
    onSaved: () => void;
}

/**
 * Where the Inbox sends email from. Mirrors the Email Marketing sender setup: either reuse the
 * marketing sender (already verified there) or verify a separate address for the Inbox on SES
 * (CUD-5584 / CUD-5588). Saving "own" starts verification and the inbox comes back `pending`.
 */
const EmailSenderModal: FunctionComponent<EmailSenderModalProps> = ({ inbox, apiClientWrapper, onCancel, onSaved }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const marketingSender = useSelector<RootState, MarketplaceEmailSender | null>(
        state => state.emailMarketingSender.data
    );
    const marketingLoading = useSelector<RootState, boolean>(state => state.emailMarketingSender.loading);

    const initialSource: InboxSenderSource = inbox.settings.senderSource ?? 'marketing';
    const [source, setSource] = useState<InboxSenderSource>(initialSource);
    const [email, setEmail] = useState<string>(initialSource === 'own' ? inbox.fromIdentity : '');
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [marketingFetched, setMarketingFetched] = useState(false);

    useEffect(() => {
        dispatch(fetchEmailMarketingSenderThunk(apiClientWrapper))
            .catch(() => undefined)
            .finally(() => setMarketingFetched(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const marketingVerified = marketingSender?.enabled === true && marketingSender.status === 'verified';
    const marketingEmail = marketingSender?.senderEmail ?? null;
    // the marketing option is shown but not selectable until a verified sender exists (or while we check)
    const marketingAvailable = marketingFetched && !marketingLoading && marketingVerified;

    useEffect(() => {
        // land the user on the option they can actually use
        if (marketingFetched && !marketingAvailable && source === 'marketing') {
            setSource('own');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketingFetched, marketingAvailable]);

    const trimmedEmail = email.trim();
    const isValidEmail = useMemo(() => validateSenderEmail(trimmedEmail), [trimmedEmail]);
    const emailError =
        touched && trimmedEmail.length > 0 && !isValidEmail ? 'Please enter a valid email address.' : null;

    const isOwnUnchanged = source === 'own' && initialSource === 'own' && trimmedEmail === inbox.fromIdentity;
    const canSubmit =
        !saving &&
        (source === 'marketing'
            ? initialSource !== 'marketing' && marketingAvailable
            : isValidEmail && !isOwnUnchanged);

    const handleSave = async () => {
        setTouched(true);
        if (!canSubmit) return;

        setSaving(true);
        setError(null);
        try {
            await dispatch(
                updateInboxThunk(apiClientWrapper, {
                    inboxId: inbox.id,
                    senderSource: source,
                    fromIdentity: source === 'own' ? trimmedEmail : undefined
                })
            );
            onSaved();
        } catch (requestError) {
            setError(inboxRequestErrorMessage(requestError, 'The sender could not be saved. Try again.'));
            setSaving(false);
        }
    };

    const marketingDescription =
        !marketingFetched || marketingLoading
            ? 'Checking your Email Marketing sender…'
            : marketingAvailable && marketingEmail
            ? `Replies go out from ${marketingEmail}, the address already verified for your campaigns.`
            : 'No verified Email Marketing sender yet.';

    return (
        <GenericModal
            className={styles.modal}
            onClose={saving ? undefined : onCancel}
            footer={
                <div className={styles.footer}>
                    <Button
                        size="large"
                        className={styles.footerButton}
                        label="Cancel"
                        variant="black"
                        onClick={onCancel}
                        disabled={saving}
                    />
                    <Button
                        size="large"
                        className={styles.footerButton}
                        label={source === 'own' ? 'Save & Verify' : 'Save'}
                        variant="green"
                        onClick={handleSave}
                        isLoading={saving}
                        disabled={!canSubmit}
                    />
                </div>
            }
        >
            <div className={styles.body}>
                <h3 className={styles.title}>Inbox email sender</h3>
                <p className={styles.subtitle}>Choose the address your client replies go out from.</p>

                <div className={styles.options}>
                    <button
                        type="button"
                        className={clsx(
                            styles.option,
                            source === 'marketing' && styles.optionSelected,
                            !marketingAvailable && styles.optionDisabled
                        )}
                        onClick={() => setSource('marketing')}
                        disabled={saving || !marketingAvailable}
                    >
                        <div className={styles.optionRadio}>
                            <Radio
                                checked={source === 'marketing'}
                                onChange={() => setSource('marketing')}
                            />
                        </div>
                        <div className={styles.optionBody}>
                            <span className={styles.optionTitle}>Use my Email Marketing sender</span>
                            <span className={styles.optionDescription}>{marketingDescription}</span>
                        </div>
                    </button>
                    {marketingFetched && !marketingLoading && !marketingAvailable && (
                        <Link
                            to={MarketingEmailsPath}
                            className={styles.optionLink}
                        >
                            Set up a sender in Email Marketing
                        </Link>
                    )}

                    <button
                        type="button"
                        className={clsx(styles.option, source === 'own' && styles.optionSelected)}
                        onClick={() => setSource('own')}
                        disabled={saving}
                    >
                        <div className={styles.optionRadio}>
                            <Radio
                                checked={source === 'own'}
                                onChange={() => setSource('own')}
                            />
                        </div>
                        <div className={styles.optionBody}>
                            <span className={styles.optionTitle}>Use a different address for the Inbox</span>
                            <span className={styles.optionDescription}>
                                We will send a verification email to this address. Replies use it once verified.
                            </span>
                        </div>
                    </button>
                    {source === 'own' && (
                        <div className={styles.optionInput}>
                            <Input
                                label="Inbox sender email"
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                                onBlur={() => setTouched(true)}
                                error={emailError}
                                autoFocus
                                required
                                autoComplete="off"
                                disabled={saving}
                            />
                        </div>
                    )}
                </div>

                {error && <p className={styles.error}>{error}</p>}
            </div>
        </GenericModal>
    );
};

export default EmailSenderModal;
